import * as React from 'react';

import { formatDateTime } from '@/domains/survey';
import type {
  IPlacementValue,
  IRankingActor,
  IRankingAddress,
  IRankingEvent,
  IRankingHistory,
  IRankingPlacement
} from '../../api/ranking-contracts';
import { rankingsApi } from '../../api/rankings-api';
import { absent } from '../../components/account-facts';
import { withBatch } from '../../components/history-batch';
import { HistoryPopover, type IHistoryPage, type IHistoryRow } from '../../components/history-popover';
import { wordFor } from '../../figures';
import { ORIGIN_WORDS, RANKING_EVENT_WORDS } from '../../rankings';

/**
 * One placement's history under Rankings (the advisor record page's F6, W8; its plan's R6):
 * survey-api's `…/rankings/{survey}/{list}/{year}/events`, turned into the kit's `HistoryPopover`
 * rows, beside the cell's pencil — ten a page, page 1 read afresh on each opening. The shape of
 * `figures-history.tsx`, one fold below.
 *
 * - **Every cell carries it, an empty one included**: a removed placement is an empty cell, and an
 *   empty cell survey-api never wrote is asked too — the history is where a removal is said. Above
 *   the rows, the placement as it is now — the history's own `placement`, read with the page, never
 *   the table's: "<edition>: place <n>, <territory>", "Removed" for a row survey-api keeps with no
 *   place, or "No placement here, ever" where no row holds the address. **Where it came from** in
 *   words — a migrated placement's Salesforce record as selectable text (no link: no Salesforce
 *   base URL is configured, and Salesforce is being switched off) or "Entered by staff" — and who
 *   set it, when. This is where the table's face sends what it does not draw (the roadmap's D11),
 *   the edition's name included (Q1 ruled (a)).
 * - **A row is one event as sent**, newest first, never sorted: When, Who, What ("Set", "Removed",
 *   any other kind as spelled), From, To ("place <n>, <territory>", "—" for none), and Reason,
 *   which the popover draws only where a row carries one.
 * - **Who**: an account by its email, "an account since removed" without one, the role as spelled
 *   for an event no account made — research-history's words.
 *
 * Rejected: a history only on a filled cell (figures' rule) — a removed placement's cell is empty
 * on the face, and its history is the one place the removal is said.
 */

/** A place and territory as a history cell draws them, "—" for none. */
function placed(value: Nullable<IPlacementValue>): React.ReactNode {
  if (value === null) return absent('—');
  return value.territory === null
    ? `place ${value.place}`
    : `place ${value.place}, ${value.territory}`;
}

/** Who made a change, as a history says it: the email, else why there is none. */
export function who(actor: Nullable<IRankingActor>): React.ReactNode {
  if (actor === null) return absent('No account');
  if (actor.email) return actor.email;
  if (!actor.accountId) return absent(`No account (${actor.role})`);
  return absent('an account since removed');
}

/** Where a placement came from, in words — the origin's, then its Salesforce source's members. */
function provenance(placement: IRankingPlacement): React.ReactNode {
  const said = wordFor(ORIGIN_WORDS, placement.origin);
  const { source } = placement;
  if (source?.kind !== 'salesforce') return said;
  const record = [source.object, source.field].filter(Boolean).join(' ');
  return (
    <span className="select-text">
      {said}
      {record && `: ${record}`}
      {source.recordId && `, record ${source.recordId}`}
    </span>
  );
}

/** The placement as it is now, above the rows. */
function StoredNow({ placement }: { placement: Nullable<IRankingPlacement> }) {
  if (placement === null) {
    return (
      <p data-stored className="text-muted-foreground">
        No placement here, ever
      </p>
    );
  }
  const { place, territory } = placement;
  return (
    <div className="grid gap-1" data-stored>
      <div>
        {place === null ? (
          'Removed'
        ) : (
          <>
            <span className="text-muted-foreground">{'Stored now: '}</span>
            {`${placement.name}: ${placed({ place, territory })}`}
          </>
        )}
      </div>
      <div>{provenance(placement)}</div>
      <div className="text-xs text-muted-foreground">
        {placement.actor ? (
          <>
            Set by {who(placement.actor)}, {formatDateTime(placement.setAt)}
          </>
        ) : (
          <>Set {formatDateTime(placement.setAt)}</>
        )}
      </div>
    </div>
  );
}

/** survey-api's history document as the popover's page (R6). */
function rankingHistoryPage(history: IRankingHistory): IHistoryPage {
  const items = history.items.map(
    (event: IRankingEvent, index): IHistoryRow => ({
      // An event carries no id; its place in the page is unique within the page.
      key: `${history.page}:${index}`,
      at: event.at,
      who: who(event.actor),
      what: withBatch(wordFor(RANKING_EVENT_WORDS, event.kind), event.batch),
      from: placed(event.old),
      to: placed(event.new),
      reason: event.reason
    })
  );
  return {
    items,
    page: history.page,
    pageSize: history.pageSize,
    total: history.total,
    above: <StoredNow placement={history.placement} />
  };
}

/** The history button of one cell about one member. */
export function RankingHistory({
  participantId,
  address,
  label,
  name,
  origin
}: {
  participantId: string;
  address: IRankingAddress;
  /** The cell's name — the edition's, or "<list>, <year>" for an empty cell. */
  label: string;
  /** The member's name — "History of <label> for <name>". */
  name: string;
  /** The drawn placement's origin, null for an empty cell — an untouched migrated placement has
   *  no event, and says why. */
  origin: Nullable<string>;
}) {
  return (
    <HistoryPopover
      label={label}
      name={name}
      emptyText={
        origin === 'migration'
          ? 'No change since it was imported.'
          : 'No change to this placement is recorded.'
      }
      read={async (page, signal) =>
        rankingHistoryPage(await rankingsApi.history(participantId, address, page, signal))
      }
    />
  );
}
